import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '~/api/client'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { PageHeader } from '~/components/PageHeader'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardHeader, CardTitle } from '~/components/ui/card'
import { Label } from '~/components/ui/label'
import { Select } from '~/components/ui/select'
import { Skeleton } from '~/components/ui/skeleton'
import { useToast } from '~/components/ui/toast'
import { Toggle } from '~/components/ui/toggle'

// 거버넌스 > 에이전트 설정: 프로파일별 활성 프롬프트(prompt_ref)·바인딩 모델·도구 on/off를
// 한 화면에서 보여주고 편집한다. (구 AgentsPage 통합) — GET/PUT /agents/{profile}.
interface AgentConfig {
    profile: string
    promptRef: string
    // 이 프로파일의 채팅 모델 바인딩(chat.<profile>) 값. 읽기 전용으로 표시만 하며
    // 편집은 「모델」 페이지에서 use-case별로 한다. 바인딩이 없으면 null로 내려온다.
    model: string | null
    tools: Record<string, boolean>
}

interface PromptVersion {
    ref: string
    profile: string
    version: number
    active: boolean
}

// 프로파일 키 → 한글 라벨. 백엔드 프로파일 키와 동일하게 유지한다.
const PROFILES = [
    'certification',
    'consultation',
    'maintenance',
    'monitoring',
] as const
type Profile = (typeof PROFILES)[number]

const PROFILE_LABEL: Record<Profile, string> = {
    certification: '인증하기',
    consultation: '상담하기',
    maintenance: '유지하기',
    monitoring: '모니터링',
}

export function AgentSettingsPage() {
    const toast = useToast()
    const [configs, setConfigs] = useState<Record<string, AgentConfig>>({})
    const [prompts, setPrompts] = useState<PromptVersion[]>([])
    const [drafts, setDrafts] = useState<Record<string, AgentConfig>>({})
    const [loading, setLoading] = useState(true)
    const [pendingSave, setPendingSave] = useState<Profile | null>(null)

    const load = () => {
        setLoading(true)
        Promise.all(
            PROFILES.map((p) =>
                apiClient
                    .get<AgentConfig>(`/agents/${p}`)
                    .then((c) => [p, c] as const)
                    .catch(() => null)
            )
        )
            .then((entries) => {
                const next: Record<string, AgentConfig> = {}
                for (const e of entries) if (e) next[e[0]] = e[1]
                setConfigs(next)
                setDrafts(next)
            })
            .catch((e) => toast.error((e as Error).message))
            .finally(() => setLoading(false))
        apiClient
            .get<PromptVersion[]>('/prompts')
            .then(setPrompts)
            .catch((e) => toast.error((e as Error).message))
    }

    useEffect(load, [])

    const setDraft = (profile: string, patch: Partial<AgentConfig>) =>
        setDrafts((d) => ({ ...d, [profile]: { ...d[profile], ...patch } }))

    // 모델은 읽기 전용이라 편집 대상이 아니다. prompt_ref·tools 변경만 dirty로 본다.
    const isDirty = (profile: string) => {
        const cfg = configs[profile]
        const draft = drafts[profile]
        if (!cfg || !draft) return false
        return (
            cfg.promptRef !== draft.promptRef ||
            JSON.stringify(cfg.tools) !== JSON.stringify(draft.tools)
        )
    }

    const confirmSave = async () => {
        if (!pendingSave) return
        const profile = pendingSave
        const draft = drafts[profile]
        setPendingSave(null)
        try {
            const updated = await apiClient.put<AgentConfig>(
                `/agents/${profile}`,
                {
                    promptRef: draft.promptRef,
                    tools: draft.tools,
                }
            )
            setConfigs((c) => ({ ...c, [profile]: updated }))
            setDrafts((d) => ({ ...d, [profile]: updated }))
            toast.success(
                `${PROFILE_LABEL[profile]} 에이전트 설정을 저장했어요.`
            )
        } catch (err) {
            toast.error((err as Error).message)
        }
    }

    return (
        <div>
            <PageHeader
                title='에이전트 설정'
                description='프로파일별 활성 프롬프트 · 도구를 설정해요. 바인딩 모델은 읽기 전용이에요. (크로스서비스 에이전트 거버넌스)'
            />

            {loading ? (
                <div className='grid gap-4 lg:grid-cols-2'>
                    {PROFILES.map((profile) => (
                        <Card key={profile} className='p-5'>
                            <Skeleton className='h-5 w-32' />
                            <div className='mt-4 space-y-3'>
                                <Skeleton className='h-9 w-full' />
                                <Skeleton className='h-9 w-full' />
                                <Skeleton className='h-16 w-full' />
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className='grid gap-4 lg:grid-cols-2'>
                    {PROFILES.map((profile) => {
                        const draft = drafts[profile]
                        const profilePrompts = prompts.filter(
                            (p) => p.profile === profile
                        )
                        return (
                            <Card key={profile} className='flex flex-col'>
                                <CardHeader className='flex-row items-center justify-between'>
                                    <div className='flex items-center gap-2'>
                                        <CardTitle>
                                            {PROFILE_LABEL[profile]}
                                        </CardTitle>
                                        <Badge tone='neutral'>{profile}</Badge>
                                    </div>
                                    {draft && isDirty(profile) ? (
                                        <Badge tone='warning'>변경됨</Badge>
                                    ) : null}
                                </CardHeader>

                                {!draft ? (
                                    <p className='text-muted-foreground px-5 pb-5 text-sm'>
                                        설정을 불러올 수 없어요.
                                    </p>
                                ) : (
                                    <div className='flex flex-col gap-4 px-5 pb-5'>
                                        <div className='flex flex-col gap-1.5'>
                                            <Label
                                                htmlFor={`${profile}-prompt`}>
                                                활성 프롬프트 (prompt_ref)
                                            </Label>
                                            <Select
                                                id={`${profile}-prompt`}
                                                size='sm'
                                                value={draft.promptRef}
                                                onChange={(e) =>
                                                    setDraft(profile, {
                                                        promptRef:
                                                            e.target.value,
                                                    })
                                                }
                                                aria-label={`${PROFILE_LABEL[profile]} 활성 프롬프트 선택`}>
                                                {profilePrompts.length === 0 ? (
                                                    <option
                                                        value={draft.promptRef}>
                                                        {draft.promptRef ||
                                                            '(프롬프트 없음)'}
                                                    </option>
                                                ) : (
                                                    <>
                                                        {!profilePrompts.some(
                                                            (p) =>
                                                                p.ref ===
                                                                draft.promptRef
                                                        ) && draft.promptRef ? (
                                                            <option
                                                                value={
                                                                    draft.promptRef
                                                                }>
                                                                {
                                                                    draft.promptRef
                                                                }{' '}
                                                                (목록 외)
                                                            </option>
                                                        ) : null}
                                                        {profilePrompts.map(
                                                            (p) => (
                                                                <option
                                                                    key={p.ref}
                                                                    value={
                                                                        p.ref
                                                                    }>
                                                                    {p.ref} · v
                                                                    {p.version}
                                                                    {p.active
                                                                        ? ' (활성)'
                                                                        : ''}
                                                                </option>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                            </Select>
                                        </div>

                                        <div className='flex flex-col gap-1.5'>
                                            <span className='krds-label text-foreground select-none'>
                                                바인딩 모델
                                            </span>
                                            {draft.model ? (
                                                <span className='text-foreground text-sm'>
                                                    {draft.model}
                                                </span>
                                            ) : (
                                                <span className='text-muted-foreground text-sm'>
                                                    채팅 기본값(chat.default)
                                                </span>
                                            )}
                                            <Link
                                                to='/governance/models'
                                                className='text-primary text-xs font-medium underline-offset-2 hover:underline'>
                                                모델에서 변경
                                            </Link>
                                            <p className='text-muted-foreground text-xs'>
                                                모델은 「모델」 페이지에서
                                                use-case별로 관리해요.
                                            </p>
                                        </div>

                                        <div className='flex flex-col gap-2'>
                                            {/* 도구는 단일 컨트롤이 아닌 토글 목록의 그룹 라벨이라
                                            htmlFor 대상이 없어 span으로 둔다(KRDS 라벨 시각 유지). */}
                                            <span className='krds-label text-foreground select-none'>
                                                도구
                                            </span>
                                            {Object.keys(draft.tools).length ===
                                            0 ? (
                                                <p className='text-muted-foreground text-sm'>
                                                    등록된 도구가 없어요.
                                                </p>
                                            ) : (
                                                <ul className='flex flex-col gap-1'>
                                                    {Object.entries(
                                                        draft.tools
                                                    ).map(([tool, enabled]) => (
                                                        <li
                                                            key={tool}
                                                            className='flex items-center justify-between gap-3 py-0.5'>
                                                            <span className='text-foreground text-sm'>
                                                                {tool}
                                                            </span>
                                                            <Toggle
                                                                checked={
                                                                    enabled
                                                                }
                                                                onCheckedChange={(
                                                                    v
                                                                ) =>
                                                                    setDraft(
                                                                        profile,
                                                                        {
                                                                            tools: {
                                                                                ...draft.tools,
                                                                                [tool]: v,
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                                aria-label={`${tool} 도구 ${enabled ? '끄기' : '켜기'}`}
                                                            />
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>

                                        <div className='flex justify-end gap-2 pt-1'>
                                            <Button
                                                variant='outline'
                                                size='sm'
                                                disabled={!isDirty(profile)}
                                                onClick={() =>
                                                    setDrafts((d) => ({
                                                        ...d,
                                                        [profile]:
                                                            configs[profile],
                                                    }))
                                                }>
                                                되돌리기
                                            </Button>
                                            <Button
                                                size='sm'
                                                disabled={!isDirty(profile)}
                                                onClick={() =>
                                                    setPendingSave(profile)
                                                }>
                                                저장
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        )
                    })}
                </div>
            )}

            <ConfirmDialog
                open={pendingSave !== null}
                title='에이전트 설정 저장'
                description={
                    pendingSave
                        ? `'${PROFILE_LABEL[pendingSave]}' 프로파일의 프롬프트·도구 설정을 적용할까요?`
                        : undefined
                }
                confirmLabel='저장'
                onConfirm={confirmSave}
                onCancel={() => setPendingSave(null)}
            />
        </div>
    )
}
